// Datenzugriffe der Hausmeister-Ansicht.
//
// Alles hier läuft mit den Rechten des angemeldeten Hausmeisters — die Datenbank
// gibt nur Tickets heraus, die ihm freigegeben wurden (ticket_freigegeben()).
// Es gibt bewusst KEINE Umgehung über eine Service-Rolle.
import { supabase } from '../supabase/client';

export interface HausmeisterTicket {
  id: string;
  ticket_nummer: number | null;
  titel: string;
  beschreibung: string | null;
  status: string;
  prioritaet: string | null;
  faellig_am: string | null;
  objekt_id: string | null;
  objekt?: { bezeichnung: string | null; strasse: string | null; hausnummer: string | null; plz: string | null; stadt: string | null } | null;
  hinweis?: string | null;
}

export interface Material {
  id: string;
  ticket_id: string;
  bezeichnung: string;
  menge: number;
  einheit: string;
  einzelpreis: number | null;
  erfasst_von: string;
  created_at: string;
}

/** Die mir freigegebenen Tickets. */
export async function ladeMeineTickets(): Promise<HausmeisterTicket[]> {
  const { data, error } = await supabase
    .from('tickets')
    .select('id, ticket_nummer, titel, beschreibung, status, prioritaet, faellig_am, objekt_id, objekt:objekte(bezeichnung, strasse, hausnummer, plz, stadt)')
    .order('faellig_am', { ascending: true, nullsFirst: false });
  if (error) { console.error('Tickets laden fehlgeschlagen', error.message); return []; }
  // PostgREST liefert eingebettete 1:1-Bezüge als Array — hier auf ein Objekt bringen.
  return (data || []).map((t: any) => ({
    ...t,
    objekt: Array.isArray(t.objekt) ? t.objekt[0] || null : t.objekt || null,
  })) as HausmeisterTicket[];
}

/** Statuswechsel — mehr als den Status lässt die Datenbank nicht zu. */
export async function statusSetzen(ticketId: string, status: string): Promise<string | null> {
  const { error } = await supabase.from('tickets').update({ status }).eq('id', ticketId);
  return error ? error.message : null;
}

/** Notiz als (nicht interner) Kommentar — die Verwaltung liest mit. */
export async function notizSchreiben(ticketId: string, autorId: string, text: string): Promise<string | null> {
  const { error } = await supabase.from('ticket_kommentare').insert({
    ticket_id: ticketId, autor_id: autorId, inhalt: text.trim(), ist_intern: false,
  });
  return error ? error.message : null;
}

export async function ladeNotizen(ticketId: string): Promise<{ id: string; inhalt: string; autor_id: string; created_at: string }[]> {
  const { data, error } = await supabase
    .from('ticket_kommentare')
    .select('id, inhalt, autor_id, created_at')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: false });
  if (error) { console.error('Notizen laden fehlgeschlagen', error.message); return []; }
  return data || [];
}

export type FotoArt = 'vorher' | 'nachher' | null;

/** Foto vom Einsatz — gleicher Weg wie in der Verwaltung (Bucket ticket-anhaenge).
 *  `fotoArt` vorher/nachher = im Eigentümerportal sichtbar (#564); null = nur intern. */
export async function fotoHochladen(ticketId: string, meineId: string, datei: File, fotoArt: FotoArt = null): Promise<string | null> {
  if (datei.size > 25 * 1024 * 1024) return 'Die Datei ist größer als 25 MB.';
  const endung = (datei.name.split('.').pop() || 'jpg').toLowerCase().slice(0, 5);
  const pfad = `${ticketId}/${Date.now()}_${Math.random().toString(36).slice(7)}.${endung}`;
  const { error: upErr } = await supabase.storage.from('ticket-anhaenge').upload(pfad, datei, { upsert: false });
  if (upErr) return `Hochladen fehlgeschlagen: ${upErr.message}`;
  const { error } = await supabase.from('ticket_anhaenge').insert({
    ticket_id: ticketId, dateiname: datei.name, dateipfad: pfad,
    dateityp: datei.type, dateigroesse: datei.size, hochgeladen_von: meineId, foto_art: fotoArt,
  });
  return error ? error.message : null;
}

export async function ladeFotos(ticketId: string): Promise<{ id: string; dateiname: string; dateipfad: string; foto_art: FotoArt }[]> {
  const { data, error } = await supabase
    .from('ticket_anhaenge').select('id, dateiname, dateipfad, foto_art').eq('ticket_id', ticketId)
    .order('created_at', { ascending: false });
  if (error) { console.error('Fotos laden fehlgeschlagen', error.message); return []; }
  return data || [];
}

export async function anhangUrl(pfad: string): Promise<string | null> {
  const { data } = await supabase.storage.from('ticket-anhaenge').createSignedUrl(pfad, 3600);
  return data?.signedUrl || null;
}

// ── Material ─────────────────────────────────────────────────────────────────

export async function ladeMaterial(ticketId: string): Promise<Material[]> {
  const { data, error } = await supabase
    .from('ticket_material').select('*').eq('ticket_id', ticketId).order('created_at');
  if (error) { console.error('Material laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Material[];
}

export async function materialErfassen(args: {
  ticketId: string; meineId: string; bezeichnung: string; menge: number; einheit: string; einzelpreis?: number | null;
}): Promise<string | null> {
  const { error } = await supabase.from('ticket_material').insert({
    ticket_id: args.ticketId, erfasst_von: args.meineId, bezeichnung: args.bezeichnung.trim(),
    menge: args.menge, einheit: args.einheit, einzelpreis: args.einzelpreis ?? null,
  });
  return error ? error.message : null;
}

export async function materialLoeschen(id: string): Promise<boolean> {
  const { error } = await supabase.from('ticket_material').delete().eq('id', id);
  return !error;
}

// ── Freigabe (Verwaltungsseite) ──────────────────────────────────────────────

export async function ladeHausmeister(): Promise<{ id: string; full_name: string | null; email: string | null }[]> {
  const { data, error } = await supabase
    .from('profiles').select('id, full_name, email').eq('role', 'hausmeister').order('full_name');
  if (error) { console.error('Hausmeister laden fehlgeschlagen', error.message); return []; }
  return data || [];
}

export async function ladeFreigaben(ticketId: string): Promise<{ id: string; hausmeister_id: string; hinweis: string | null }[]> {
  const { data, error } = await supabase
    .from('ticket_hausmeister').select('id, hausmeister_id, hinweis').eq('ticket_id', ticketId);
  if (error) { console.error('Freigaben laden fehlgeschlagen', error.message); return []; }
  return data || [];
}

export async function freigeben(ticketId: string, hausmeisterId: string, vonId: string, hinweis?: string): Promise<string | null> {
  const { error } = await supabase.from('ticket_hausmeister').insert({
    ticket_id: ticketId, hausmeister_id: hausmeisterId, freigegeben_von: vonId, hinweis: hinweis || null,
  });
  return error ? error.message : null;
}

export async function freigabeEntziehen(id: string): Promise<boolean> {
  const { error } = await supabase.from('ticket_hausmeister').delete().eq('id', id);
  return !error;
}
